import {
  isNative,
  useGetAverageReviewsForApplicationQuery,
  useV2GetCategoryByIdQuery,
  useV2ListBundlesQuery,
} from "@stepapp/core";
import {
  Block,
  Divider,
  LinePlaceholder,
  palette,
  RatingScale,
  spacing,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import { useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import { ScrollView, StyleSheet, useWindowDimensions } from "react-native";
import { Contact, Stepcover, Testimonials, WhatMakesUsDifferent } from "../../src/components";
import OtherCategories from "../../src/components/Homepage/OtherCategories";
import { useAppSelector } from "../../src/hooks";
import CategoryBanner from "./CategoryBanner";
import ServiceItem from "./ServiceItem";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: todo
const CategoryScreen = () => {
  const { t } = useTranslation();
  const { mobileOnly, md } = useBreakpoints();
  const { width: screenWidth } = useWindowDimensions();
  const { id } = useLocalSearchParams<{ id: string }>();
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);

  const { data, loading } = useV2ListBundlesQuery({
    variables: { localizationId, categoryId: id },
  });

  const { data: categoryItem } = useV2GetCategoryByIdQuery({
    variables: { id },
  });

  const category = categoryItem?.v2GetCategoryById;

  const { data: avgReviews } = useGetAverageReviewsForApplicationQuery();

  const averageRating = avgReviews?.getAverageReviewsForApplication.average
    ? `${avgReviews?.getAverageReviewsForApplication.average?.toFixed(2)}/5`
    : null;
  const amountOfReviews = `(${avgReviews?.getAverageReviewsForApplication.total} ${t("screens.dashboard.testimonials")})`;

  const showPlaceholders = loading && !data?.v2ListBundles;
  const paddingHorizontal = isNative ? 2.5 : 0;

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <CategoryBanner
        categoryDesktopImg={category?.desktopUrl}
        categoryMobileImg={category?.mobileUrl}
      />

      <Block paddingHorizontal={paddingHorizontal}>
        <Typography variant="newH2" style={styles.title}>
          {category?.name}
        </Typography>
        <Block marginLeft={-0.5} marginTop={md ? 2 : 1} row alignCenter>
          <RatingScale currentRating={5} padding={0} size={16} width={2.5} />
          {averageRating && (
            <Typography decoration="underline" variant="label" style={styles.rating}>
              {averageRating} {amountOfReviews}
            </Typography>
          )}
        </Block>
      </Block>

      {!md && (
        <>
          <Block marginTop={3} paddingHorizontal={paddingHorizontal}>
            <WhatMakesUsDifferent
              desktopImage={category?.extraDesktopUrl}
              mobileImage={category?.extraMobileUrl}
            />
          </Block>

          <Divider style={styles.divider} color={palette.common.mediumGray} />

          <Typography variant="paragraph" style={styles.additionalPadding}>
            {t("screens.category.chooseYourService")}
          </Typography>
        </>
      )}

      <Block marginTop={md ? 2.25 : 0} row={md} paddingHorizontal={paddingHorizontal}>
        <Block flex={1} marginRight={md ? 3 : 0}>
          {showPlaceholders && (
            <LinePlaceholder
              borderRadius={1}
              height={md ? 24 : 18}
              width={(screenWidth - (md ? 412 : 40)) / 8}
              marginTop={3}
            />
          )}
          {data?.v2ListBundles?.map((bundle, index) => (
            <ServiceItem
              {...bundle}
              testID={`service-item-${index}`}
              key={bundle?.id}
              spacious={!mobileOnly}
            />
          ))}

          {!md && <Stepcover />}

          <OtherCategories />
        </Block>

        {md && (
          <Block width={45.5} marginTop={md ? 3 : 0}>
            <WhatMakesUsDifferent
              desktopImage={category?.extraDesktopUrl}
              mobileImage={category?.extraMobileUrl}
            />

            <Stepcover />

            <Contact />
          </Block>
        )}
        {!md && <Contact />}
      </Block>

      <Divider style={md ? styles.dividerHigh : styles.divider} color={palette.common.mediumGray} />

      <Block paddingHorizontal={paddingHorizontal}>
        <Testimonials />
      </Block>
    </ScrollView>
  );
};

export default CategoryScreen;

const styles = StyleSheet.create({
  additionalPadding: {
    paddingHorizontal: spacing.unit * (isNative ? 2.5 : 0),
  },
  container: {
    paddingBottom: spacing.unit * 6,
  },
  title: {
    height: spacing.unit * 3.5,
  },
  rating: {
    marginLeft: spacing.unit,
  },
  divider: {
    marginTop: spacing.unit * 3,
    marginBottom: spacing.unit * 2,
  },
  dividerHigh: {
    marginTop: spacing.unit * 9.5,
    marginBottom: spacing.unit * 6,
  },
});
